import React from 'react';
import {
  FormControl,
  Box,
  SelectChangeEvent,
  Autocomplete,
  TextField,
  Collapse
} from '@mui/material';
import {
  DataSource,
  ShowAs,
  GroupBy,
  DateRange,
  Entity,
  ALL_OPTION,
  StatusFilter,
  ALL_STATUSES_OPTION
} from '../hooks/useChartData';
import SelectField from '../../NewUI/Components/CustomUIElements/SelectField';
import { sharedClasses } from '../../NewUI/Components/CustomUIElements/sharedClasses';
import { FilterChips } from './FilterChips';

interface ChartFiltersProps {
  dataSource: DataSource | '';
  showAs: ShowAs | '';
  groupBy: GroupBy | '';
  dateRange: DateRange;
  selectedEntities: Entity[];
  selectedStatuses: StatusFilter[];
  entities: Entity[];
  availableStatuses: StatusFilter[];
  hasData: boolean;
  onDataSourceChange: (event: SelectChangeEvent<DataSource>) => void;
  onShowAsChange: (event: SelectChangeEvent<ShowAs>) => void;
  onGroupByChange: (event: SelectChangeEvent<GroupBy>) => void;
  onDateRangeChange: (event: SelectChangeEvent<DateRange>) => void;
  onEntitiesChange: (_: React.SyntheticEvent, newValue: Entity[]) => void;
  onStatusesChange: (_: React.SyntheticEvent, newValue: StatusFilter[]) => void;
  onPresetSelect: (preset: {
    dataSource: DataSource;
    showAs: ShowAs;
    groupBy: GroupBy;
    dateRange: DateRange;
  }) => void;
  getShowAsOptions: () => { value: ShowAs; label: string }[];
  getGroupByOptions: () => { value: GroupBy; label: string }[];
  getDateRangeOptions: () => { value: DateRange; label: string }[];
}

export const ChartFilters: React.FC<ChartFiltersProps> = ({
  dataSource,
  showAs,
  groupBy,
  dateRange,
  selectedEntities,
  selectedStatuses,
  entities,
  availableStatuses,
  hasData,
  onDataSourceChange,
  onShowAsChange,
  onGroupByChange,
  onDateRangeChange,
  onEntitiesChange,
  onStatusesChange,
  onPresetSelect,
  getShowAsOptions,
  getGroupByOptions,
  getDateRangeOptions
}) => {
  // State to prevent UI flicker when switching between jobs/applications via preset chips
  // This keeps form fields visible during the transition instead of collapsing/expanding
  const [isPresetTransitioning, setIsPresetTransitioning] = React.useState(false);

  const handleEntitiesChange = (_: React.SyntheticEvent, newValue: Entity[]) => {
    if (newValue.length === 0) {
      // If nothing is selected, default to "All"
      onEntitiesChange(_, [ALL_OPTION]);
      return;
    }

    const lastSelected = newValue[newValue.length - 1];
    const hasAll = lastSelected.id === 0;
    const hasSpecific = newValue.some((e) => e.id !== 0);

    if (hasAll) {
      // If "All" is selected, clear other selections
      onEntitiesChange(_, [ALL_OPTION]);
    } else if (hasSpecific) {
      // If specific entities are selected, remove "All" option
      onEntitiesChange(
        _,
        newValue.filter((e) => e.id !== 0)
      );
    }
  };

  const handleStatusesChange = (_: React.SyntheticEvent, newValue: StatusFilter[]) => {
    if (newValue.length === 0) {
      // If nothing is selected, default to "All States"
      onStatusesChange(_, [ALL_STATUSES_OPTION]);
      return;
    }

    const lastSelected = newValue[newValue.length - 1];
    const hasAll = lastSelected.id === '0';
    const hasSpecific = newValue.some((s) => s.id !== '0');

    if (hasAll) {
      // If "All States" is selected, clear other selections
      onStatusesChange(_, [ALL_STATUSES_OPTION]);
    } else if (hasSpecific) {
      // If specific states are selected, remove "All States" option
      onStatusesChange(
        _,
        newValue.filter((s) => s.id !== '0')
      );
    }
  };

  const availableOptions = React.useMemo(() => {
    // Deduplicate entities by ID to prevent duplicate key warnings
    const uniqueEntities = entities.filter(
      (entity, index, self) => index === self.findIndex((e) => e.id === entity.id)
    );

    const hasSpecific = selectedEntities.some((e) => e.id !== 0);
    return hasSpecific ? uniqueEntities : [ALL_OPTION, ...uniqueEntities];
  }, [entities, selectedEntities]);

  const availableStatusOptions = React.useMemo(() => {
    // Deduplicate statuses by ID to prevent duplicate key warnings
    const uniqueStatuses = availableStatuses.filter(
      (status, index, self) => index === self.findIndex((s) => s.id === status.id)
    );

    const hasSpecific = selectedStatuses.some((s) => s.id !== '0');
    return hasSpecific ? uniqueStatuses.filter((s) => s.id !== '0') : uniqueStatuses;
  }, [availableStatuses, selectedStatuses]);

  // Handler for preset chip selection that prevents UI flicker during data source transitions
  const handlePresetSelect = (preset: {
    dataSource: DataSource;
    showAs: ShowAs;
    groupBy: GroupBy;
    dateRange: DateRange;
  }) => {
    setIsPresetTransitioning(true);
    onPresetSelect(preset);

    // Reset the transition flag after a short delay to allow the new values to be set
    setTimeout(() => {
      setIsPresetTransitioning(false);
    }, 400);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <FormControl sx={{ flex: 1, minWidth: 200 }}>
          <SelectField
            id="data-source-select"
            label="Data Source"
            options={[
              { value: 'jobs', label: 'Jobs' },
              { value: 'applications', label: 'Applications' }
            ]}
            value={dataSource}
            onChange={(value) =>
              onDataSourceChange({ target: { value } } as SelectChangeEvent<DataSource>)
            }
          />
        </FormControl>
        <FormControl sx={{ flex: 1, minWidth: 200 }}>
          <SelectField
            id="date-range-select"
            label="Date Range"
            options={getDateRangeOptions()}
            value={dateRange}
            onChange={(value) =>
              onDateRangeChange({ target: { value } } as SelectChangeEvent<DateRange>)
            }
          />
        </FormControl>
        <Collapse
          in={Boolean(dataSource) || isPresetTransitioning}
          timeout={300}
          sx={{ flex: 1, minWidth: 200 }}
        >
          <FormControl fullWidth>
            <SelectField
              id="show-as-select"
              label="Show As"
              options={getShowAsOptions()}
              value={showAs}
              onChange={(value) =>
                onShowAsChange({ target: { value } } as SelectChangeEvent<ShowAs>)
              }
            />
          </FormControl>
        </Collapse>
        <Collapse
          in={Boolean(showAs) || isPresetTransitioning}
          timeout={300}
          sx={{ flex: 1, minWidth: 200 }}
        >
          <FormControl fullWidth>
            <SelectField
              id="group-by-select"
              label="Group By"
              options={getGroupByOptions()}
              value={groupBy}
              onChange={(value) =>
                onGroupByChange({ target: { value } } as SelectChangeEvent<GroupBy>)
              }
            />
          </FormControl>
        </Collapse>

        {/* Status filter - only show for applications when groupBy is 'status' or jobs when groupBy is 'job_state' */}
        {((dataSource === 'applications' && showAs && groupBy === 'status') ||
          (dataSource === 'jobs' && showAs && groupBy === 'job_state')) && (
          <Collapse in={true} timeout={300} sx={{ minWidth: 300 }}>
            <Autocomplete
              disablePortal
              disableCloseOnSelect
              multiple
              getOptionLabel={(option) => option.name}
              blurOnSelect={false}
              options={availableStatusOptions}
              value={selectedStatuses}
              sx={{ ...sharedClasses.formAutocomplete, marginTop: 3 }}
              loading={availableStatuses.length === 0}
              loadingText="Loading statuses..."
              noOptionsText="No statuses found"
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase();
                return options.filter((option) => option.name.toLowerCase().includes(searchTerm));
              }}
              renderOption={(props, option) => (
                <li {...props} key={`status-${option.id}`}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={dataSource === 'applications' ? 'Select Statuses' : 'Select States'}
                  placeholder="Please select"
                  sx={sharedClasses.formAutocomplete}
                />
              )}
              onChange={(event, value) => handleStatusesChange(event, value)}
            />
          </Collapse>
        )}
        {hasData && dataSource && showAs && groupBy && (
          <FormControl sx={{ minWidth: 300 }}>
            <Autocomplete
              disablePortal
              disableCloseOnSelect
              multiple
              getOptionLabel={(option) => option.name}
              blurOnSelect={false}
              options={availableOptions}
              value={selectedEntities}
              sx={{ ...sharedClasses.formAutocomplete, marginTop: 3 }}
              loading={entities.length === 0}
              loadingText="Loading entities..."
              noOptionsText="No entities found"
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, { inputValue }) => {
                const searchTerm = inputValue.toLowerCase();
                return options.filter((option) => option.name.toLowerCase().includes(searchTerm));
              }}
              renderOption={(props, option) => (
                <li {...props} key={`entity-${option.id}`}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Entities"
                  placeholder="Please select"
                  sx={sharedClasses.formAutocomplete}
                />
              )}
              onChange={(event, value) => handleEntitiesChange(event, value)}
            />
          </FormControl>
        )}
      </Box>
      <FilterChips onPresetSelect={handlePresetSelect} />
    </Box>
  );
};
